import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { checkForErrors } from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import {
  ApiCredentialsData,
  loadApiCredentialsData,
} from './load-api-credentials';

export interface CreateApiCredentialResult extends ApiCredentialsData {
  /** The freshly created key + secret, shown once to the user. */
  createdKey: { key: string; secret: string } | null;
  cb?: (created?: { key: string; secret: string } | null) => void;
}

/** Creates an API key on the chosen pype, then re-fetches all credentials. */
export const createApiCredential = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<CreateApiCredentialResult | void> => {
  if (event.type !== 'createApiCredential') {
    return;
  }

  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId || !customerId) {
    throwErrorWithId('Project ID and Customer ID are required');
    return;
  }

  const { name, description, pypeId, env, cb } = event.payload;

  const postMutation = {
    live: urqlGql.postPypeKeyLive,
    sandbox: urqlGql.postPypeKeySandbox,
  }[env];

  const response = await postMutation({
    projectId,
    customerId,
    pypeId,
    apiKeyInput: { friendlyName: name, description },
  });
  checkForErrors(response);

  const created =
    response.data?.pypesPostCustomersPypesKeys ??
    response.data?.pypesPostCustomersPypesKeysSandbox;

  const data = await loadApiCredentialsData(ctx);

  return {
    ...data,
    createdKey:
      created?.key && created?.secret
        ? { key: created.key, secret: created.secret }
        : null,
    cb,
  };
};
